import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/Badge';
import { PLATFORM_SERVICES, calculateServiceMetrics, getTotalMonthlyCost, ServiceCost } from '../../api/serviceCosts';
import { apiList } from '../../api/apiClient';

const fmtMoney = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ServiceCostsTab: React.FC<{ totalRevenueCents: number }> = ({ totalRevenueCents }) => {
  const [services, setServices] = useState<ServiceCost[]>(PLATFORM_SERVICES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [properties, calls, videoCalls] = await Promise.all([
          apiList('properties').catch(() => []),
          apiList('call_history').catch(() => []),
          apiList('video_calls').catch(() => []),
        ]);

        const phoneNumbers = properties.filter((p: any) => p.telnyx_number).length;
        const talkMinutes = (calls as any[]).reduce((s: number, c: any) => s + (c.duration_seconds || c.duration || 0), 0) / 60;
        const videoMinutes = (videoCalls as any[]).reduce((s: number, v: any) => s + (v.duration_seconds || v.duration || 0), 0) / 60;

        const enriched = calculateServiceMetrics(PLATFORM_SERVICES, {
          phoneNumbers,
          talkMinutes: Math.round(talkMinutes),
          videoMinutes: Math.round(videoMinutes),
          totalRevenue: totalRevenueCents,
          transactionCount: 10, // estimate
        });
        setServices(enriched);
      } catch (e) {
        console.error('Failed to calculate service costs:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [totalRevenueCents]);

  const totalCost = getTotalMonthlyCost(services);
  const totalRevenue = totalRevenueCents / 100;
  const totalProfit = totalRevenue - totalCost;
  const overallMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  // Group by category
  const categories = Array.from(new Set(services.map(s => s.category)));

  if (loading) return <div className="p-8 text-center text-gray-400">Calculating service costs...</div>;

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 p-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-xs text-gray-500">Monthly Revenue</p>
          <p className="text-2xl font-bold text-green-600">{fmtMoney(totalRevenue)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-xs text-gray-500">Monthly Costs</p>
          <p className="text-2xl font-bold text-red-600">{fmtMoney(totalCost)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-xs text-gray-500">Monthly Profit</p>
          <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtMoney(totalProfit)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <p className="text-xs text-gray-500">Margin</p>
          <p className={`text-2xl font-bold ${overallMargin >= 50 ? 'text-green-600' : overallMargin >= 20 ? 'text-yellow-600' : 'text-red-600'}`}>
            {overallMargin.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Services by Category */}
      {categories.map(cat => (
        <div key={cat} className="mb-4">
          <div className="px-4 py-2 bg-gray-50 border-y border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase">{cat}</h4>
          </div>
          <div className="divide-y divide-gray-100">
            {services.filter(s => s.category === cat).map(s => (
              <div key={s.id} className="grid grid-cols-7 gap-2 px-4 py-3 text-sm items-center">
                <div>
                  <span className="font-medium text-gray-900">{s.name}</span>
                  <p className="text-xs text-gray-400">{s.provider}</p>
                </div>
                <span className="text-gray-600 text-xs">{s.cost_label}</span>
                <span className="font-mono text-xs text-gray-400 truncate" title={s.api_proxy_url}>{s.api_proxy_url}</span>
                <span className="text-gray-600">{s.active_count != null ? s.active_count.toLocaleString() : '—'}</span>
                <span className="font-medium text-red-600">{fmtMoney(s.monthly_cost || 0)}</span>
                <span className="font-medium text-green-600">{fmtMoney(s.monthly_revenue || 0)}</span>
                <span>
                  <Badge color={!s.monthly_cost ? 'gray' : (s.monthly_cost < 50) ? 'green' : (s.monthly_cost < 200) ? 'yellow' : 'red'}>
                    {s.monthly_cost ? fmtMoney(s.monthly_cost) + '/mo' : 'Included'}
                  </Badge>
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Column headers */}
      <div className="px-4 py-1 text-xs text-gray-400">
        Columns: Service | Unit Cost | API Proxy | Active Count | Monthly Cost | Monthly Revenue | Status
      </div>
    </div>
  );
};
